import { Button, H5, H6 } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogPage,
} from "../../../../../../components/modal/Dialog";
import { useFullPageDialogContext } from "../../../../../../components/modal/useFullPageDialogContext";
import { useDebounce } from "use-debounce";
import { useProductList } from "../../../../../../hooks/api/v2/product/useProductList";
import { InfiniteList } from "../../../../../../components/infiniteLoading/InfiniteList";
import { ListElement, SpinnerLegacy } from "../../../../../../components";
import { InputContainer } from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import { useEffect, useState } from "react";
import { ProductListResponseType } from "@sellar-io/api-v2/types";
import {
  ItemDiscountForm,
  ItemDiscountFormSubmitType,
} from "../forms/ItemDiscountForm";
import { useSupplierRelationshipProductItemDiscountCreate } from "../../../../../../hooks/api/v2/supplierRelationshipProductItemDiscount/useSupplierRelationshipProductItemDiscountCreate";
import { apiV2AssertError } from "../../../../../../hooks/api/v2/apiV2Request";
import { Alert } from "../../../../../../components/alert/Alert";
import { useItemDiscountCardContext } from "../BusinessConnectionItemDiscountsCard.shared";
import { calculateBiggestContainerDiscountPrice } from "@sellar-io/common";
import {
  AddItemDiscountDialogPage,
  AddItemDiscountDialogContext,
  useAddItemDiscountDialogContext,
} from "./AddItemDiscountDialog.shared";

export type ConnectionProps = {
  buyerName: string;
  retailerId: number;
  connectionId: number;
};

const ProductItemListElement = ({
  product,
  onClick,
}: {
  product: ProductListResponseType[number];
  onClick: () => void;
}) => {
  const { itemDiscountList, setEditingItemDiscount } =
    useItemDiscountCardContext();
  const { onDismiss } = useFullPageDialogContext();

  const existingItemDiscount = itemDiscountList.find(
    (itemDiscount) => itemDiscount.ProductItemId === product.id,
  );
  const itemDiscountAlreadyAdded = existingItemDiscount !== undefined;

  return (
    <ListElement
      className={"p-4"}
      onClick={
        itemDiscountAlreadyAdded
          ? () => {
              setEditingItemDiscount(existingItemDiscount);
              onDismiss?.();
            }
          : onClick
      }
      icon={itemDiscountAlreadyAdded ? "PENCIL" : "PLUS"}
    >
      {product.name}
    </ListElement>
  );
};

/**
 * DIALOG PAGES
 */

const SearchPageContent = () => {
  const {
    connection: { retailerId, buyerName },
  } = useItemDiscountCardContext();
  const { searchQuery, setSearchQuery, setProduct, setProductItem } =
    useAddItemDiscountDialogContext();
  const { onDismiss, setPage } = useFullPageDialogContext();

  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);

  const productListQuery = useProductList(
    {
      search: debouncedSearchQuery,
      sortBy: "name",
      sortDir: "asc",
      query: [
        {
          type: "Products",
          modifier: debouncedSearchQuery ? "IS_NULL" : "NOT_NULL",
        },
        {
          type: "showAllProducts",
          modifier: "EQUALS",
          value: true,
        },
      ],
    },
    {
      RetailerId: retailerId,
    },
  );

  return (
    <>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
        title={"Add product item discount"}
        description={`Set a custom price for an individual product item at ${buyerName}`}
      >
        <InputContainer>
          <InputText
            autoFocus
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={"Search for a product..."}
          />
        </InputContainer>
      </DialogNavigation>
      <DialogContent className={"pb-4"}>
        {productListQuery.isLoading ? (
          <SpinnerLegacy />
        ) : !productListQuery.data?.pages[0]?.[0] ? (
          <div>No products found</div>
        ) : (
          <InfiniteList
            query={productListQuery}
            className={"divide-y rounded-lg border border-solid"}
          >
            {productListQuery.data.pages.map((page) =>
              page.map((product) =>
                debouncedSearchQuery ? (
                  <ProductItemListElement
                    key={product.id}
                    product={product}
                    onClick={() => {
                      setProduct(undefined);
                      setProductItem({ ...product, name: product.name ?? "" });
                      setPage(AddItemDiscountDialogPage.PRODUCT_ITEM_DISCOUNT);
                    }}
                  />
                ) : (
                  <ListElement
                    key={product.id}
                    className={"p-4"}
                    onClick={() => {
                      setProduct(product);
                      setPage(AddItemDiscountDialogPage.PRODUCT_ITEM_LIST);
                    }}
                  >
                    <div>
                      <H5>{product.name}</H5>
                      <H6>
                        {product.kind}
                        {" // "}
                        {product.abv}
                      </H6>
                    </div>
                  </ListElement>
                ),
              ),
            )}
          </InfiniteList>
        )}
      </DialogContent>
    </>
  );
};

const ProductItemListPageContent = () => {
  const {
    connection: { retailerId, buyerName },
  } = useItemDiscountCardContext();

  const { product, setProductItem } = useAddItemDiscountDialogContext();
  const { setPage } = useFullPageDialogContext();

  if (!product) throw new Error("No product selected.");

  const productListQuery = useProductList(
    {
      query: [
        {
          type: "ParentId",
          modifier: "EQUALS",
          value: product.id,
        },
      ],
    },
    {
      RetailerId: retailerId,
    },
  );

  return (
    <>
      <DialogNavigation
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={() => setPage(AddItemDiscountDialogPage.SEARCH)}
        title={"Add product item discount"}
        description={`Set a custom price for an individual product item at ${buyerName}`}
      />
      <DialogContent className={"pb-4"}>
        {productListQuery.isLoading ? (
          <SpinnerLegacy />
        ) : (
          <InfiniteList
            query={productListQuery}
            className={"divide-y rounded-lg border border-solid"}
          >
            {productListQuery.data?.pages.map((page) =>
              page.map((product, i) => (
                <ProductItemListElement
                  key={i}
                  product={product}
                  onClick={() => {
                    setProductItem({ ...product, name: product.name ?? "" });
                    setPage(AddItemDiscountDialogPage.PRODUCT_ITEM_DISCOUNT);
                  }}
                />
              )),
            )}
          </InfiniteList>
        )}
      </DialogContent>
    </>
  );
};

const ProductItemPageContent = () => {
  const {
    connection: { buyerName },
    containerTypeDiscountList,
  } = useItemDiscountCardContext();
  const { setPage } = useFullPageDialogContext();
  const { onSubmit, setProductItem, productItem, product } =
    useAddItemDiscountDialogContext();

  if (!productItem) throw new Error("No product item selected.");
  if (!productItem.ProductPriceTier) throw new Error("No product prict tier.");

  const biggestContainerDiscountPrice = calculateBiggestContainerDiscountPrice({
    connectionDiscounts: containerTypeDiscountList,
    product: {
      containerType: productItem.containerType,
      productPriceTier: {
        price: productItem.ProductPriceTier.fullPrice,
      },
    },
  });

  const productItemFullPrice = productItem.ProductPriceTier.fullPrice;

  return (
    <>
      <DialogNavigation
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={() =>
          setPage(
            product
              ? AddItemDiscountDialogPage.PRODUCT_ITEM_LIST
              : AddItemDiscountDialogPage.SEARCH,
          )
        }
        title={"Add product item discount"}
        description={`Set a custom price for an individual product item at ${buyerName}`}
      />
      <DialogContent>
        <ItemDiscountForm
          onRemoveItem={() => {
            setPage(AddItemDiscountDialogPage.SEARCH);
            setProductItem(undefined);
          }}
          productName={productItem.name ?? ""}
          itemPriceBeforeDiscount={productItemFullPrice}
          containerTypeDiscountAmount={
            productItemFullPrice - biggestContainerDiscountPrice
          }
          onSubmit={(itemDiscount) =>
            onSubmit({ ...itemDiscount, productItemId: productItem.id })
          }
        >
          <Button className={"w-full"} type={"submit"}>
            Add
          </Button>
        </ItemDiscountForm>
      </DialogContent>
    </>
  );
};

/**
 * PAGES CONTAINER
 */

const AddItemDiscountDialogContent = ({
  onSubmit,
}: {
  onSubmit: (
    productItemDiscount: ItemDiscountFormSubmitType & { productItemId: number },
  ) => void;
}) => {
  const { setPage } = useFullPageDialogContext();
  const [searchQuery, setSearchQuery] = useState("");

  const [product, setProduct] = useState<ProductListResponseType[number]>();
  const [productItem, setProductItem] =
    useState<ProductListResponseType[number]>();

  useEffect(() => {
    return () => {
      setPage(AddItemDiscountDialogPage.SEARCH);
    };
  }, [setPage]);

  return (
    <AddItemDiscountDialogContext.Provider
      value={{
        searchQuery,
        setSearchQuery,
        onSubmit: (productItemDiscount) => {
          onSubmit(productItemDiscount);
        },
        product,
        setProduct,
        productItem,
        setProductItem,
      }}
    >
      <DialogPage page={AddItemDiscountDialogPage.SEARCH}>
        <SearchPageContent />
      </DialogPage>

      <DialogPage page={AddItemDiscountDialogPage.PRODUCT_ITEM_LIST}>
        <ProductItemListPageContent />
      </DialogPage>

      <DialogPage page={AddItemDiscountDialogPage.PRODUCT_ITEM_DISCOUNT}>
        <ProductItemPageContent />
      </DialogPage>
    </AddItemDiscountDialogContext.Provider>
  );
};

/**
 * DIALOG
 */

export const AddItemDiscountDialog = ({
  active,
  onDismiss,
}: {
  active: boolean;
  onDismiss: () => void;
}) => {
  const {
    connection: { id },
  } = useItemDiscountCardContext();

  const connectionItemDiscountCreate =
    useSupplierRelationshipProductItemDiscountCreate();

  return (
    <Dialog
      fullHeight={true}
      active={active}
      onDismiss={onDismiss}
      initPage={AddItemDiscountDialogPage.SEARCH}
    >
      {connectionItemDiscountCreate.isError ? (
        <Alert
          type={"error"}
          message={
            apiV2AssertError(connectionItemDiscountCreate.error)?.response?.data
              .message ?? "Unknown error occurred."
          }
        />
      ) : null}
      <AddItemDiscountDialogContent
        onSubmit={(productItemDiscount) => {
          connectionItemDiscountCreate.mutate(
            {
              SupplierRetailerConnectionId: id,
              ProductItemId: productItemDiscount.productItemId,
              discountAmount: productItemDiscount.discountAmount,
              type: productItemDiscount.type,
            },
            {
              onSuccess: () => onDismiss(),
            },
          );
        }}
      />
    </Dialog>
  );
};
